import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_URL } from "../../apiConfig";

const T = {
  ink: "#1a1a1c",
  ink2: "#4a4a50",
  ink3: "#86868b",
  hair: "rgba(20,20,22,0.07)",
  hair2: "rgba(20,20,22,0.04)",
  surface: "rgba(255,255,255,0.72)",
  accent: "#1e3a8a",
  ok: "#1E8E3E",
  warn: "#B25E09",
  danger: "#C5221F",
  shadowMd: "0 6px 24px -8px rgba(20,20,22,0.08),0 2px 6px rgba(20,20,22,0.04)",
  font: "'Geist',-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif",
  r: 18,
};

const cardStyle = {
  background: T.surface,
  backdropFilter: "blur(28px) saturate(180%)",
  WebkitBackdropFilter: "blur(28px) saturate(180%)",
  border: `1px solid ${T.hair}`,
  borderRadius: T.r,
  boxShadow: T.shadowMd,
};

const EVENT_OPTIONS = [
  { value: "", label: "Todos os eventos" },
  { value: "login_success", label: "Login OK" },
  { value: "login_failed", label: "Login falhou" },
  { value: "refresh_success", label: "Refresh OK" },
  { value: "refresh_failed", label: "Refresh falhou" },
  { value: "logout", label: "Logout" },
];

function eventLabel(event) {
  const match = EVENT_OPTIONS.find((item) => item.value === event);
  return match?.label || event || "—";
}

function eventColor(event) {
  if (event?.includes("success")) return T.ok;
  if (event === "logout") return T.ink2;
  return T.danger;
}

function fmtDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch {
    return "—";
  }
}

export default function AuthAuditLogs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [event, setEvent] = useState("");
  const [email, setEmail] = useState("");
  const [ip, setIp] = useState("");

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", "50");
    if (event) params.set("event", event);
    if (email.trim()) params.set("email", email.trim());
    if (ip.trim()) params.set("ip", ip.trim());
    return params.toString();
  }, [page, event, email, ip]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");

    try {
      const response = await fetch(`${API_URL}/admin/auth-audit-logs?${queryString}`);
      const data = await response.json();

      if (!response.ok) {
        setErr(data?.message || "Erro ao carregar auditoria");
        return;
      }

      setItems(Array.isArray(data.items) ? data.items : []);
      setPages(Number(data.pages || 1));
      setTotal(Number(data.total || 0));
    } catch {
      setErr("Falha de conexão com a API");
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [event, email, ip]);

  return (
    <div style={{ fontFamily: T.font }}>
      <style>{`
        .audit-table th { text-align:left; font-weight:600; font-size:10.5px; color:${T.ink2}; letter-spacing:.06em; text-transform:uppercase; padding:10px 14px; border-bottom:1px solid ${T.hair}; }
        .audit-table td { padding:12px 14px; vertical-align:top; border-bottom:1px solid ${T.hair2}; font-size:13px; color:${T.ink}; }
        .audit-table tr:last-child td { border-bottom:none; }
        .audit-field { width:100%; min-width:0; border:1px solid ${T.hair}; border-radius:12px; padding:10px 12px; background:rgba(255,255,255,0.82); color:${T.ink}; outline:none; }
        .audit-btn { border:none; border-radius:12px; padding:10px 14px; background:${T.accent}; color:white; font-weight:600; }
        .audit-btn.ghost { background:transparent; color:${T.ink2}; border:1px solid ${T.hair}; }
      `}</style>

      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600, letterSpacing: "-0.025em", color: T.ink }}>Auditoria de Login</h1>
          <div style={{ fontSize: 13, color: T.ink3, marginTop: 3 }}>
            {total} evento(s) de autenticação registrados
          </div>
        </div>
      </div>

      <div style={{ ...cardStyle, padding: 18, marginBottom: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr 1fr auto auto", gap: 12, alignItems: "center" }}>
          <select className="audit-field" value={event} onChange={(e) => setEvent(e.target.value)}>
            {EVENT_OPTIONS.map((option) => (
              <option key={option.label} value={option.value}>{option.label}</option>
            ))}
          </select>
          <input className="audit-field" placeholder="Filtrar por e-mail" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="audit-field" placeholder="Filtrar por IP" value={ip} onChange={(e) => setIp(e.target.value)} />
          <button className="audit-btn" onClick={load} disabled={loading}>
            {loading ? "Carregando..." : "Atualizar"}
          </button>
          <button
            className="audit-btn ghost"
            onClick={() => {
              setEvent("");
              setEmail("");
              setIp("");
              setPage(1);
            }}
          >
            Limpar
          </button>
        </div>
      </div>

      <div style={{ ...cardStyle, overflow: "hidden" }}>
        {err ? (
          <div style={{ padding: 16, color: T.danger }}>{err}</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="audit-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Evento</th>
                  <th>E-mail</th>
                  <th>Perfil</th>
                  <th>IP</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 18, color: T.ink3 }}>Nenhum log encontrado.</td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item._id}>
                      <td>{fmtDate(item.createdAt)}</td>
                      <td>
                        <span style={{ color: eventColor(item.event), fontWeight: 600 }}>
                          {eventLabel(item.event)}
                        </span>
                      </td>
                      <td>{item.email || "—"}</td>
                      <td>{item.role || "—"}</td>
                      <td>{item.ip || "—"}</td>
                      <td style={{ color: T.ink3 }}>{item.reason || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: T.ink3 }}>
          Página {page} de {pages}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="audit-btn ghost" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page <= 1 || loading}>
            Anterior
          </button>
          <button className="audit-btn ghost" onClick={() => setPage((value) => Math.min(pages, value + 1))} disabled={page >= pages || loading}>
            Próxima
          </button>
        </div>
      </div>
    </div>
  );
}
